import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, QrCode, Download, Building, User, Plane, Calendar,
  CirclePoundSterling, Users, MapPin, CreditCard, FileExclamationPoint, ListCheck, ScrollText,
  Car,
  Mountain
} from 'lucide-react';
import { useTranslation } from '../../../i18n/i18n';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../../../api/axios';

import VoucherFlight from './VoucherFlight';
import VoucherHotel from './VoucherHotel';
import VoucherTransfer from './VoucherTransfer';
import VoucherActivity from './VoucherActivity';

const VoucherPage = ({ lang }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(lang);

  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState(null);

  const booking_url = process.env.REACT_APP_API_BOOKING_URL;

  const fetchVoucherDetail = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${booking_url}/${id}`);
      const transformedData = transformBookingData(data.result);
      setVoucherData(transformedData);
      setType(data.result.bookingType);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoucherDetail();
  }, [id]);

  const transformBookingData = (newBooking) => {
    return {
      // Basic booking info
      booking_number: newBooking.bookingNumber,
      bookingNumber: newBooking.bookingNumber,
      status: newBooking.status,
      payment_status: newBooking.paymentStatus,
      service: newBooking.bookingType,
      amount_total: newBooking.amountTotal,
      currency: newBooking.currency,
      provider_amount_total: newBooking.providerAmountTotal,
      provider_currency: newBooking.providerCurrency,
      checkin: newBooking.checkin,
      checkout: newBooking.checkout,
      duration: newBooking.duration,
      created_at: newBooking.confirmedDate,
      cancelled_at: newBooking.cancelledDate,
      payment_date_limit_at: newBooking.paymentDateLimit,
      date_limit_cancellation: newBooking.dateLimitCcancellation,

      // Client information
      holder: newBooking.holder,
      customer: newBooking.customer,
      customer_id: newBooking.customer?.id,
      customer_account_id: newBooking.customer?.id,

      // Provider information
      provider_id: newBooking.provider?.id,
      provider: newBooking.provider,

      // Product information
      product_id: newBooking.product?.id,
      product: newBooking.product,
      country: newBooking.country,
      destination: newBooking.product?.city,
      country_id: newBooking.country?.id,
      destination_id: newBooking.product?.city?.id,

      // Flight specific (if applicable)
      booking_flight: newBooking.flight ? {
        trip: newBooking.flight.trip,
        adult: newBooking.flight.adult,
        child: newBooking.flight.child,
        infant: newBooking.flight.infant,
        airline: newBooking.flight.airline,
        itineraries: newBooking.flight.itinerary?.map(it => ({
          departure: it.departure,
          arrival: it.arrival,
          segments: it.segments,
          duration: it.segments?.[0]?.duration
        })),
      } : null,

      booking_transfer: newBooking.transfer ? {
        type: newBooking.transfer.type,
        adult: newBooking.transfer.adult,
        child: newBooking.transfer.child,
        itineraries: newBooking.transfer.itinerary?.map(it => ({
          origin: it.origin,
          destination: it.destination,
          vehicle: it.vehicle,
          transferDate: it.transferDate,
          transferTime: it.transferTime,
          travelType: it.travelType,
          travelNumber: it.travelNumber
        }))
      } : null,

      booking_activity: newBooking.activity ? {
        days: newBooking.activity.days,
        adult: newBooking.activity.adult,
        child: newBooking.activity.child,
        baby: newBooking.activity.baby,
        senior: newBooking.activity.senior,
        amount: newBooking.activity.amount,
        prices: newBooking.activity.prices
      } : null,

      booking_hotel: newBooking.hotel ? {
        meal_name: newBooking.hotel.meal_name,
        meal_id: newBooking.hotel.meal_id,
        meal_plan: newBooking.hotel.meal_name,
        nights: newBooking.hotel.nights,
        rooms_count: newBooking.hotel.rooms_count,
        adult_count: newBooking.hotel.adult_count,
        child_count: newBooking.hotel.child_count,
        rooms: newBooking.hotel.rooms?.map((room, index) => ({
          id: room.id || index,
          name: room.name,
          bed_type: room.bed_type,
          count: room.count,
          adult: room.adult,
          child: room.child,
          amount: room.amount,
          amount_provider: room.amountProvider || room.prices?.[0]?.amountProvider,
          product_hotel_room_id: room.id || room.room_id,
          prices: room.prices?.map(price => ({
            date: price.date,
            amount: price.amount,
            amountProvider: price.amountProvider
          })),
          is_obsolete: room.is_obsolete || false
        })),
      } : null,

      // Passengers
      passengers: newBooking.passengers?.map(p => ({
        title: p.title,
        type: p.type,
        gender: p.gender,
        last_name: p.lastName,
        first_name: p.firstName,
        is_holder: `${p.firstName} ${p.lastName}` === newBooking.holder
      })),

      // Policies and conditions
      cancellation_policies: newBooking.cancellationPolicies?.map(cp => ({
        date: cp.date,
        amount: cp.amount,
        amountProvider: cp.amountProvider
      })),

      policies: newBooking.rateComment?.map(rc => ({
        value: rc.value
      })),

      // Additional data
      comments: newBooking.comments || [],
      extra_includes: newBooking.extraIncludes?.map(ei => ({
        value: ei.value
      })) || [],

      extra_excludes: newBooking.extraExclude?.map(ee => ({
        value: ee.value
      })) || []
    };
  };
  const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US');
    } catch {
      return t('notAvailable');
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return t('notAvailable');
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US') +
        ' ' + date.toLocaleTimeString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
    } catch {
      return t('notAvailable');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'paid':
      case 'active':
        return '#10b981';
      case 'cancelled':
      case 'refused':
      case 'expired':
        return '#ef4444';
      case 'pending':
      case 'unpaid':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'flight': return <Plane size={20} />;
      case 'hotel': return <Building size={20} />;
      case 'activity': return <Mountain size={20} />;
      case 'transfer': return <Car size={20} />;
      default: return <Building size={20} />;
    }
  };

  const generatePDF = async () => {
    try {
      toast.info(t('generatingPDF'));

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const usableWidth = pageWidth - margin * 2;

      let currentY = margin;

      const sections = document.querySelectorAll('.pdf-section');

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const imgHeight = (canvas.height * usableWidth) / canvas.width;

        if (currentY + imgHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }

        pdf.addImage(
          imgData,
          'PNG',
          margin,
          currentY,
          usableWidth,
          imgHeight
        );

        currentY += imgHeight + 5;
      }

      pdf.save(`voucher-${voucherData?.booking_number || 'unknown'}.pdf`);
      toast.success(t('pdfGenerated'));
    } catch (error) {
      console.error(error);
      toast.error(t('pdfError'));
    }
  };

  const styles = {
    container: {
      padding: '20px 40px 20px',
      background: '#f5f7fa',
      minHeight: '100vh',
      color: '#333'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '32px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#4b5563',
      transition: 'all 0.2s ease',
      marginBottom: '0px',
      width: 'fit-content',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    pageTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginLeft: 'auto'
    },
    actionButton: {
      padding: '10px 20px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    voucherContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      padding: '40px',
      position: 'relative'
    },
    voucherHeader: {
      textAlign: 'center',
      marginBottom: '32px',
      borderBottom: '2px solid #e5e7eb',
      paddingBottom: '24px'
    },
    voucherTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#7c3aed',
      margin: '0 0 16px 0',
      letterSpacing: '2px'
    },
    bookingNumber: {
      fontSize: '18px',
      color: '#6b7280',
      marginBottom: '16px',
      fontWeight: '500'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '8px 24px',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    section: {
      marginBottom: '32px',
      paddingBottom: '24px',
      borderBottom: '1px solid #e5e7eb'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '20px',
      paddingBottom: '8px',
      borderBottom: '2px solid #7c3aed',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    infoLabel: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '500'
    },
    infoValue: {
      fontSize: '16px',
      color: '#1f2937',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '16px'
    },
    tableHeader: {
      background: '#f8fafc',
      borderBottom: '2px solid #e5e7eb'
    },
    tableHeaderCell: {
      padding: '12px 16px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px',
      textTransform: 'uppercase'
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb'
    },
    tableCell: {
      padding: '12px 16px',
      fontSize: '14px',
      color: '#374151'
    },
    qrContainer: {
      textAlign: 'center',
      margin: '32px 0',
      padding: '24px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px dashed #d1d5db'
    },
    qrPlaceholder: {
      width: '150px',
      height: '150px',
      background: '#e5e7eb',
      borderRadius: '8px',
      margin: '0 auto 16px auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7280'
    },
    notes: {
      fontSize: '12px',
      color: '#6b7280',
      lineHeight: '1.6',
      marginTop: '16px'
    },
    noteItem: {
      marginBottom: '8px'
    },
    footer: {
      marginTop: '40px',
      paddingTop: '24px',
      borderTop: '1px solid #e5e7eb',
      textAlign: 'center',
      fontSize: '12px',
      color: '#9ca3af',
      lineHeight: '1.6'
    },
    loading: {
      textAlign: 'center',
      padding: '60px',
      color: '#6b7280'
    }
  };

  if (loading) {
    return <div style={styles.loading}>{t('loading')}...</div>;
  }

  if (!voucherData) {
    return <div style={styles.loading}>{t('noData')}</div>;
  }

  // Render product-specific component based on type
  const renderProductSpecificSection = () => {
    const props = { voucherData, t, formatDate, formatDateTime, styles, lang };

    switch (type) {
      case 'flight':
        return <VoucherFlight {...props} />;
      case 'hotel':
        return <VoucherHotel {...props} />;
      case 'transfer':
        return <VoucherTransfer {...props} />;
      case 'activity':
        return <VoucherActivity {...props} />;
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header with buttons */}
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={20} />
          {t('backToDetails')}
        </button>
        <h1 style={styles.pageTitle}>{t(`${type}Voucher`)}</h1>
        <div style={styles.actionButtons}>
          <button style={styles.actionButton} onClick={generatePDF}>
            <Download size={16} />
            {t('downloadPDF')}
          </button>
        </div>
      </div>

      {/* Voucher content with id for PDF */}
      <div id="voucher-content" style={styles.voucherContainer}>
        {/* Voucher Header */}
        <div className="pdf-section">
          <div style={styles.voucherHeader}>
            <h1 style={styles.voucherTitle}>{t('extranetVoucher')}</h1>
            <div style={styles.bookingNumber}>
              {t('bookingReference')}: {voucherData.booking_number}
            </div>
            <div style={{
              ...styles.statusBadge,
              background: getStatusColor(voucherData.status),
              color: 'white'
            }}>
              {t(voucherData.status?.toLowerCase()) || voucherData.status}
            </div>
          </div>
        </div>

        {/* Booking Information */}
        <div className="pdf-section">
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <Calendar size={20} />
              {t('bookingInformation')}
            </div>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>{t('serviceType')}</div>
                <div style={styles.infoValue}>
                  {getServiceIcon(voucherData.service)}
                  {t(voucherData.service)}
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>{t('checkinDate')}</div>
                <div style={styles.infoValue}>
                  <Calendar size={16} />
                  {formatDate(voucherData.checkin)}
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>{t('checkoutDate')}</div>
                <div style={styles.infoValue}>
                  <Calendar size={16} />
                  {formatDate(voucherData.checkout)}
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>{t('totalAmount')}</div>
                <div style={styles.infoValue}>
                  <CirclePoundSterling size={16} />
                  {voucherData.amount_total?.toLocaleString()} {voucherData.currency}
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>{t('paymentStatus')}</div>
                <div style={styles.infoValue}>
                  <CreditCard size={16} />
                  {t(voucherData.payment_status?.toLowerCase()) || voucherData.payment_status}
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>{t('bookingDate')}</div>
                <div style={styles.infoValue}>
                  <Calendar size={16} />
                  {formatDateTime(voucherData.created_at)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Passenger Information */}
        {voucherData.passengers && voucherData.passengers.length > 0 && (
          <div className="pdf-section">
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <Users size={20} />
                {t('passengerInformation')}
              </div>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.tableHeaderCell}>{t('name')}</th>
                    <th style={styles.tableHeaderCell}>{t('gender')}</th>
                    <th style={styles.tableHeaderCell}>{t('type')}</th>

                  </tr>
                </thead>
                <tbody>
                  {voucherData.passengers.map((passenger, index) => (
                    <tr key={index} style={styles.tableRow}>
                      <td style={styles.tableCell}>
                        {passenger.first_name} {passenger.last_name}
                      </td>
                      <td style={styles.tableCell}>{passenger.gender}</td>
                      <td style={styles.tableCell}>{passenger.type}</td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}



        {/* Product-Specific Section */}
        {renderProductSpecificSection()}

        {/* Holder Information */}
        {/* {voucherData.holder && (
          <div className="pdf-section">
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <User size={20} />
                {t('holderInformation')}
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('fullName')}</div>
                  <div style={styles.infoValue}>
                    {voucherData.holder.last_name?.toUpperCase()} {voucherData.holder.first_name}
                    {voucherData.holder.title && ` (${voucherData.holder.title})`}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('type')}</div>
                  <div style={styles.infoValue}>
                    {t(voucherData.holder.type)}
                    {voucherData.holder.gender && ` (${t(voucherData.holder.gender === 'M' ? 'male' : 'female')})`}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('birthDate')}</div>
                  <div style={styles.infoValue}>
                    {formatDate(voucherData.holder.birth_date)}
                    {voucherData.holder.age && ` (${voucherData.holder.age} ${t('years')})`}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('nationality')}</div>
                  <div style={styles.infoValue}>
                    {voucherData.holder.nationality || t('notAvailable')}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('passport')}</div>
                  <div style={styles.infoValue}>
                    {voucherData.holder.passport_number || t('notAvailable')}
                    {voucherData.holder.passport_country && ` (${voucherData.holder.passport_country})`}
                  </div>
                </div>
                {voucherData.holder.email && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('email')}</div>
                    <div style={styles.infoValue}>
                      {voucherData.holder.email}
                    </div>
                  </div>
                )}
                {voucherData.holder.phone && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('phone')}</div>
                    <div style={styles.infoValue}>
                      {voucherData.holder.phone}
                    </div>
                  </div>
                )}
                {voucherData.holder.address && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('address')}</div>
                    <div style={styles.infoValue}>
                      {voucherData.holder.address}
                    </div>
                  </div>
                )}
                {voucherData.holder.country_off_residence && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('countryOfResidence')}</div>
                    <div style={styles.infoValue}>
                      {voucherData.holder.country_off_residence}
                    </div>
                  </div>
                )}
                {voucherData.holder.passport_expiration_date && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('passportExpiration')}</div>
                    <div style={styles.infoValue}>
                      {formatDate(voucherData.holder.passport_expiration_date)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )} */}



        {/* Services Inclusions & Exclusions */}
        {(voucherData.extra_includes && voucherData.extra_includes.length > 0) ||
          (voucherData.extra_excludes && voucherData.extra_excludes.length > 0) ? (
          <div className="pdf-section">
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <ListCheck size={20} />
                {t('servicesInclusionsExclusions')}
              </div>
              <div style={styles.infoGrid}>

                {/* Included Services */}
                {voucherData.extra_includes && voucherData.extra_includes.length > 0 && (
                  <div >
                    <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                      {t('included')}
                    </div>
                    <div style={styles.notes}>
                      {voucherData.extra_includes.map((service, index) => (
                        <p key={index} style={styles.noteItem}>• {service.value}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Excluded Services */}
                {voucherData.extra_excludes && voucherData.extra_excludes.length > 0 && (
                  <div >
                    <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                      {t('excluded')}
                    </div>
                    <div style={styles.notes}>
                      {voucherData.extra_excludes.map((service, index) => (
                        <p key={index} style={styles.noteItem}>• {service.value}</p>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        ) : null}

        {(voucherData.cancellation_policies && voucherData.cancellation_policies.length > 0) ? (
          <div className="pdf-section">
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <ScrollText size={20} />
                {t('cancellationPolicies')}
              </div>
              {voucherData.cancellation_policies.map((policy, index) => (
                <div key={index} style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('deadline')}</div>
                    <div style={styles.infoValue}>
                      {formatDateTime(policy.date)}
                    </div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('fees')}</div>
                    <div style={styles.infoValue}>
                      {policy.amount} {voucherData.currency}
                    </div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>{t('providerFees')}</div>
                    <div style={styles.infoValue}>
                      {policy.amountProvider} {voucherData.provider_currency}
                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>
        ) : null}

        {/* QR Code */}
        <div className="pdf-section">
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <QrCode size={20} />
              {t('qrCode')}
            </div>
            <div style={styles.qrContainer}>
              <div style={styles.qrPlaceholder}>
                <QrCode size={64} color="#9ca3af" />
              </div>
              <div style={styles.infoLabel}>{t('scanQRCode')}</div>
            </div>
          </div>
        </div>

        {/* Contact Information - Sections minimalistes */}
        <div className="pdf-section">
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <User size={20} />
              {t('contactInformation')}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '20px' }}>

              {/* Section Produit */}
              {voucherData.product && (
                <div style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    {t('productInfo')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {voucherData.product.name && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('product')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.product.name}
                        </div>
                      </div>
                    )}
                    {voucherData.product.phone && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('phone')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.product.phone}
                        </div>
                      </div>
                    )}
                    {voucherData.product.address && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('address')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.product.address}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Section Client */}
              {voucherData.customer && (
                <div style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    {t('customer')}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {voucherData.customer.name && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('customer')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.customer.name}
                        </div>
                      </div>
                    )}
                    {voucherData.customer.phone && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('phone')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.customer.phone}
                        </div>
                      </div>
                    )}

                    {voucherData.customer.address && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('address')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.customer.address}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Section Fournisseur */}
              {voucherData.provider && (
                <div style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    {t('provider')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {voucherData.provider.name || voucherData.provider.email && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('provider')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.provider?.name ?? voucherData.provider?.email}
                        </div>
                      </div>
                    )}
                    {voucherData.provider.phone && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('phone')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.provider.phone}
                        </div>
                      </div>
                    )}
                    {voucherData.provider.address && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                          {t('address')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {voucherData.provider.address}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Note discrète */}
            <div style={{
              marginTop: '16px',
              fontSize: '12px',
      color: '#6b7280',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              {t('contactNote')}
            </div>
          </div>
        </div>



        {/* Important Notes */}
        <div className="pdf-section">
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <FileExclamationPoint size={20} />
              {t('importantNotes')}
            </div>
            <div style={styles.notes}>
              <p style={styles.noteItem}>• {t('notePresentVoucher')}</p>
              <p style={styles.noteItem}>• {t('noteKeepVoucher')}</p>
              <p style={styles.noteItem}>• {t('noteEmergencyContact')}</p>
              <p style={styles.noteItem}>• {t('noteCancellationPolicies')}</p>
              <p style={styles.noteItem}>• {t('voucherGeneratedOn')}: {formatDateTime(new Date())}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pdf-section">
          <div style={styles.footer}>
            <p>{t('officialVoucherMessage')}</p>
            <p>{t('generatedBy')} IDH EXTRANET System</p>
          </div>
        </div>
      </div>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default VoucherPage;